/**
 * Super Admin Authentication Context
 *
 * Handles authentication using the main Cognito pool (same as main app).
 * Super admin users are identified by their role attribute.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { staffApi } from '../services/staffApi';

// Multi-product + multi-stage: detect Cognito pool by hostname at runtime.
// Each product has separate dev + prod pools.
type Stage = 'dev' | 'prod';
type PoolConfig = { UserPoolId: string; ClientId: string };
const PRODUCT_COGNITO: Record<string, Record<Stage, PoolConfig>> = {
  cafemaster: {
    dev: { UserPoolId: 'us-east-2_uzrp3S7RQ', ClientId: '679riihl1r3td4k01b8ite7sr0' },
    prod: { UserPoolId: 'us-east-2_yfOta1i2s', ClientId: '5rfl1stl8c1bajo97gsfjnripa' },
  },
  agenticpos: {
    dev: { UserPoolId: 'us-west-1_aJPgzUvru', ClientId: 'n6htsn4h97mkul6s1dq1j3fe5' },
    prod: { UserPoolId: 'us-west-1_o9lUo87fN', ClientId: '6kg889kvicooh4604ne7f7immf' },
  },
};

function detectCognitoConfig(): PoolConfig {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const envPool = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const envClient = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (envPool && envClient && (hostname === 'localhost' || hostname === '127.0.0.1')) {
    return { UserPoolId: envPool, ClientId: envClient };
  }
  // Stage detection: dev./staging./stage. = dev pool; everything else = prod pool.
  const isDev = hostname === 'localhost' || hostname === '127.0.0.1'
    || hostname.includes('dev.') || hostname.includes('staging.') || hostname.includes('stage.');
  const stage: Stage = isDev ? 'dev' : 'prod';
  if (hostname.includes('rubytill')) return PRODUCT_COGNITO.agenticpos[stage];
  if (envPool && envClient) return { UserPoolId: envPool, ClientId: envClient };
  return PRODUCT_COGNITO.cafemaster[stage];
}

const COGNITO_CONFIG = detectCognitoConfig();

const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.UserPoolId,
  ClientId: COGNITO_CONFIG.ClientId,
});

// ---------------------------------------------------------------------------
// MFA recovery support (staff-service src/lib/mfa.ts has the full model).
//
// amazon-cognito-identity-js 6.x predates EMAIL_OTP: an EMAIL_OTP challenge
// falls through its dispatcher and crashes, and sendMFASelectionAnswer only
// understands SMS/TOTP. These two helpers handle exactly those steps with the
// library's own low-level client and leave every other step to the library.
// ---------------------------------------------------------------------------
interface ChallengeHandlers {
  onTotp: () => void;
  onEmail: (destination?: string) => void;
  onSuccess: (session: CognitoUserSession) => void;
  onFailure: (err: any) => void;
}

/** Route EMAIL_OTP to `onEmail` instead of letting the library crash on it. */
function interceptEmailOtp(user: CognitoUser, onEmail: (destination?: string) => void) {
  const u = user as any;
  const original = u.authenticateUserInternal.bind(u);
  u.authenticateUserInternal = (data: any, helper: any, callback: any) => {
    if (data?.ChallengeName === 'EMAIL_OTP') {
      u.Session = data.Session;
      return onEmail(data.ChallengeParameters?.CODE_DELIVERY_DESTINATION);
    }
    return original(data, helper, callback);
  };
}

function respondToChallenge(
  user: CognitoUser,
  challengeName: string,
  responses: Record<string, string>,
  h: ChallengeHandlers,
) {
  const u = user as any;
  u.client.request('RespondToAuthChallenge', {
    ChallengeName: challengeName,
    ChallengeResponses: { USERNAME: u.username, ...responses },
    ClientId: userPool.getClientId(),
    Session: u.Session,
  }, (err: any, data: any) => {
    if (err) return h.onFailure(err);
    if (data?.ChallengeName) {
      u.Session = data.Session;
      if (data.ChallengeName === 'SOFTWARE_TOKEN_MFA') return h.onTotp();
      if (data.ChallengeName === 'EMAIL_OTP') return h.onEmail(data.ChallengeParameters?.CODE_DELIVERY_DESTINATION);
      return h.onFailure(new Error(`Unexpected sign-in step: ${data.ChallengeName}`));
    }
    const session = u.getCognitoUserSession(data.AuthenticationResult);
    u.setSignInUserSession(session); // caches tokens like a normal sign-in
    h.onSuccess(session);
  });
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: CognitoUser | null;
  email: string | null;
  mfaRequired: boolean;
  newPasswordRequired: boolean;
  mfaSetupRequired: boolean;
  mfaSetupSecret: string | null;
  /** Recovery sign-in: Cognito emailed a one-time code. */
  emailCodeRequired: boolean;
  emailCodeDestination: string | null;
  /** Fresh recovery codes to show ONCE; sign-in finishes when acknowledged. */
  recoveryCodes: string[] | null;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  verifyMFA: (code: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  verifyMfaSetup: (code: string) => Promise<void>;
  verifyEmailCode: (code: string) => Promise<void>;
  acknowledgeRecoveryCodes: () => void;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    email: null,
    mfaRequired: false,
    newPasswordRequired: false,
    mfaSetupRequired: false,
    mfaSetupSecret: null,
    emailCodeRequired: false,
    emailCodeDestination: null,
    recoveryCodes: null,
    error: null,
  });

  // Temporary storage for MFA flow
  const [mfaUser, setMfaUser] = useState<CognitoUser | null>(null);
  // Temporary storage for new password flow
  const [newPasswordUser, setNewPasswordUser] = useState<CognitoUser | null>(null);

  // Helper to store tokens in localStorage so apiService can use them
  const storeTokens = (session: CognitoUserSession) => {
    const idToken = session.getIdToken().getJwtToken();
    const refreshToken = session.getRefreshToken().getToken();
    localStorage.setItem('jwtToken', idToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  };

  // Check for existing session on mount
  useEffect(() => {
    const currentUser = userPool.getCurrentUser();

    if (currentUser) {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) {
          setState((s) => ({ ...s, isLoading: false }));
          return;
        }

        // Store tokens for apiService
        storeTokens(session);

        // Fetch user attributes to get email (getUsername returns UUID)
        currentUser.getUserAttributes((attrErr: Error | undefined, attrs: CognitoUserAttribute[] | undefined) => {
          const email = attrs?.find(a => a.Name === 'email')?.Value || currentUser.getUsername();
          // A session that exists without an active authenticator (a recovery
          // sign-in whose tab was closed mid-setup) must finish setup before
          // the panel opens. A failed lookup never locks anyone out.
          currentUser.getUserData((dataErr, data) => {
            if (!dataErr && data && data.PreferredMfaSetting !== 'SOFTWARE_TOKEN_MFA') {
              startForcedEnrolment(currentUser, email);
              return;
            }
            setState({
            isAuthenticated: true,
            isLoading: false,
            user: currentUser,
            email,
            mfaRequired: false,
            newPasswordRequired: false,
            mfaSetupRequired: false,
            mfaSetupSecret: null,
            emailCodeRequired: false,
            emailCodeDestination: null,
            recoveryCodes: null,
            error: null,
          });
          });
        });
      });
    } else {
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, []);

  // Signed in WITHOUT an active authenticator (recovery email sign-in, or a
  // session found on reload): issue a new authenticator secret and hold the
  // panel shut until verifyMfaSetup completes.
  const startForcedEnrolment = (user: CognitoUser, email: string) => {
    user.associateSoftwareToken({
      associateSecretCode: (secretCode: string) => {
        setMfaUser(user);
        setState((s) => ({
          ...s,
          isAuthenticated: false,
          isLoading: false,
          email,
          mfaRequired: false,
          emailCodeRequired: false,
          emailCodeDestination: null,
          mfaSetupRequired: true,
          mfaSetupSecret: secretCode,
          error: null,
        }));
      },
      onFailure: (err: Error) => {
        setState((s) => ({ ...s, isLoading: false, error: err.message || 'Failed to start authenticator setup' }));
      },
    });
  };

  // Challenge outcomes shared by sign-in, new-password and method selection.
  const challengeHandlers = (
    user: CognitoUser, email: string, resolve: () => void, reject: (e: any) => void,
  ): ChallengeHandlers => ({
    onTotp: () => {
      setMfaUser(user);
      setState((s) => ({ ...s, isLoading: false, newPasswordRequired: false, mfaRequired: true, email }));
      resolve();
    },
    onEmail: (destination) => {
      setMfaUser(user);
      setState((s) => ({
        ...s, isLoading: false, newPasswordRequired: false, mfaRequired: false,
        emailCodeRequired: true, emailCodeDestination: destination || null, email,
      }));
      resolve();
    },
    onSuccess: (session) => {
      storeTokens(session);
      startForcedEnrolment(user, email);
      resolve();
    },
    onFailure: (err) => {
      setState((s) => ({ ...s, isLoading: false, error: err?.message || 'Authentication failed' }));
      reject(err);
    },
  });

  // SELECT_MFA_TYPE means the account has no PREFERRED factor. Always answer
  // with the authenticator; only an account that has never verified one
  // (a brand-new invitee) falls back to the email code, and must then enrol.
  const answerSelectMfa = (user: CognitoUser, email: string, resolve: () => void, reject: (e: any) => void) => {
    const h = challengeHandlers(user, email, resolve, reject);
    respondToChallenge(user, 'SELECT_MFA_TYPE', { ANSWER: 'SOFTWARE_TOKEN_MFA' }, {
      ...h,
      onFailure: () => respondToChallenge(user, 'SELECT_MFA_TYPE', { ANSWER: 'EMAIL_OTP' }, h),
    });
  };

  // Verify the one-time email code of a recovery sign-in.
  const verifyEmailCode = useCallback(async (code: string): Promise<void> => {
    if (!mfaUser) throw new Error('No sign-in in progress');
    setState((s) => ({ ...s, isLoading: true, error: null }));
    const email = state.email || '';
    return new Promise((resolve, reject) => {
      respondToChallenge(mfaUser, 'EMAIL_OTP', { EMAIL_OTP_CODE: code }, {
        ...challengeHandlers(mfaUser, email, resolve, reject),
        onFailure: (err) => {
          setState((s) => ({ ...s, isLoading: false, error: err?.message || 'Invalid code' }));
          reject(err);
        },
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaUser, state.email]);

  // The recovery codes were shown and saved — open the panel.
  const acknowledgeRecoveryCodes = useCallback(() => {
    setState((s) => ({ ...s, recoveryCodes: null, isAuthenticated: true }));
  }, []);

  // Login function
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setState((s) => ({ ...s, isLoading: true, error: null }));

    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    // Use USER_PASSWORD_AUTH flow (SRP fails on email-as-username pools)
    user.setAuthenticationFlowType('USER_PASSWORD_AUTH');

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    return new Promise((resolve, reject) => {
      interceptEmailOtp(user, challengeHandlers(user, email, resolve, reject).onEmail);
      user.authenticateUser(authDetails, {
        selectMFAType: () => answerSelectMfa(user, email, resolve, reject),

        onSuccess: (session) => {
          // Store tokens for apiService
          storeTokens(session);
          setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            email,
            mfaRequired: false,
            newPasswordRequired: false,
            mfaSetupRequired: false,
            mfaSetupSecret: null,
            emailCodeRequired: false,
            emailCodeDestination: null,
            recoveryCodes: null,
            error: null,
          });
          resolve();
        },

        onFailure: (err) => {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: err.message || 'Authentication failed',
          }));
          reject(err);
        },

        totpRequired: () => {
          setMfaUser(user);
          setState((s) => ({
            ...s,
            isLoading: false,
            mfaRequired: true,
            email,
          }));
          resolve();
        },

        newPasswordRequired: () => {
          setNewPasswordUser(user);
          setState((s) => ({
            ...s,
            isLoading: false,
            newPasswordRequired: true,
            email,
          }));
          resolve();
        },

        mfaSetup: () => {
          // MFA not yet configured - get TOTP secret from Cognito
          user.associateSoftwareToken({
            associateSecretCode: (secretCode: string) => {
              setMfaUser(user);
              setState((s) => ({
                ...s,
                isLoading: false,
                mfaSetupRequired: true,
                mfaSetupSecret: secretCode,
                email,
              }));
              resolve();
            },
            onFailure: (err: Error) => {
              setState((s) => ({
                ...s,
                isLoading: false,
                error: err.message || 'Failed to setup MFA',
              }));
              reject(err);
            },
          });
        },
      });
    });
  }, []);

  // Verify MFA code
  const verifyMFA = useCallback(async (code: string): Promise<void> => {
    if (!mfaUser) {
      throw new Error('No MFA session active');
    }

    setState((s) => ({ ...s, isLoading: true, error: null }));

    return new Promise((resolve, reject) => {
      mfaUser.sendMFACode(
        code,
        {
          onSuccess: (session) => {
            // Store tokens for apiService
            storeTokens(session);
            // Fetch user attributes to get email (getUsername returns UUID)
            mfaUser.getUserAttributes((attrErr: Error | undefined, attrs: CognitoUserAttribute[] | undefined) => {
              const email = attrs?.find(a => a.Name === 'email')?.Value || mfaUser.getUsername();
              setState({
                isAuthenticated: true,
                isLoading: false,
                user: mfaUser,
                email,
                mfaRequired: false,
                newPasswordRequired: false,
                mfaSetupRequired: false,
                mfaSetupSecret: null,
                emailCodeRequired: false,
                emailCodeDestination: null,
                recoveryCodes: null,
                error: null,
              });
              setMfaUser(null);
              resolve();
            });
          },

          onFailure: (err) => {
            setState((s) => ({
              ...s,
              isLoading: false,
              error: err.message || 'Invalid MFA code',
            }));
            reject(err);
          },
        },
        'SOFTWARE_TOKEN_MFA'
      );
    });
  }, [mfaUser]);

  // Complete new password challenge
  const completeNewPassword = useCallback(async (newPassword: string): Promise<void> => {
    if (!newPasswordUser) {
      throw new Error('No new password session active');
    }

    setState((s) => ({ ...s, isLoading: true, error: null }));

    return new Promise((resolve, reject) => {
      const npEmail = state.email || '';
      interceptEmailOtp(newPasswordUser, challengeHandlers(newPasswordUser, npEmail, resolve, reject).onEmail);
      newPasswordUser.completeNewPasswordChallenge(newPassword, {}, {
        selectMFAType: () => answerSelectMfa(newPasswordUser, npEmail, resolve, reject),

        onSuccess: (session) => {
          storeTokens(session);
          newPasswordUser.getUserAttributes((attrErr: Error | undefined, attrs: CognitoUserAttribute[] | undefined) => {
            const email = attrs?.find(a => a.Name === 'email')?.Value || newPasswordUser.getUsername();
            setState({
              isAuthenticated: true,
              isLoading: false,
              user: newPasswordUser,
              email,
              mfaRequired: false,
              newPasswordRequired: false,
              mfaSetupRequired: false,
              mfaSetupSecret: null,
              emailCodeRequired: false,
              emailCodeDestination: null,
              recoveryCodes: null,
              error: null,
            });
            setNewPasswordUser(null);
            resolve();
          });
        },

        totpRequired: () => {
          setMfaUser(newPasswordUser);
          setNewPasswordUser(null);
          setState((s) => ({
            ...s,
            isLoading: false,
            newPasswordRequired: false,
            mfaRequired: true,
          }));
          resolve();
        },

        mfaSetup: () => {
          // MFA setup required - get TOTP secret from Cognito
          newPasswordUser.associateSoftwareToken({
            associateSecretCode: (secretCode: string) => {
              setMfaUser(newPasswordUser);
              setNewPasswordUser(null);
              setState((s) => ({
                ...s,
                isLoading: false,
                newPasswordRequired: false,
                mfaSetupRequired: true,
                mfaSetupSecret: secretCode,
              }));
              resolve();
            },
            onFailure: (err: Error) => {
              setState((s) => ({
                ...s,
                isLoading: false,
                error: err.message || 'Failed to setup MFA',
              }));
              reject(err);
            },
          });
        },

        onFailure: (err) => {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: err.message || 'Failed to set new password',
          }));
          reject(err);
        },
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPasswordUser, state.email]);

  // Verify MFA setup - user enters TOTP code from authenticator app to complete setup
  const verifyMfaSetup = useCallback(async (code: string): Promise<void> => {
    if (!mfaUser) {
      throw new Error('No MFA setup session active');
    }

    setState((s) => ({ ...s, isLoading: true, error: null }));

    return new Promise((resolve, reject) => {
      mfaUser.verifySoftwareToken(code, 'TOTP Device', {
        onSuccess: (session: any) => {
          // After TOTP verification, set TOTP as preferred MFA
          mfaUser.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (err: any) => {
            if (err) {
              // Never report success here: a verified token with no preference
              // leaves the account half-enrolled — Cognito keeps asking for a
              // code that no authenticator entry can produce.
              setState((s) => ({
                ...s,
                isLoading: false,
                error: `Your authenticator was verified but could not be activated (${err.message}). Do not close this page — ask another super admin to reset your MFA.`,
              }));
              reject(err);
              return;
            }
            // Now get a fresh session
            mfaUser.getSession((sessionErr: Error | null, newSession: CognitoUserSession | null) => {
              if (sessionErr || !newSession?.isValid()) {
                // MFA setup succeeded but session may need re-login
                setState((s) => ({
                  ...s,
                  isLoading: false,
                  mfaSetupRequired: false,
                  mfaSetupSecret: null,
                  error: 'MFA setup complete. Please sign in again.',
                }));
                setMfaUser(null);
                resolve();
                return;
              }
              storeTokens(newSession);
              mfaUser.getUserAttributes(async (attrErr: Error | undefined, attrs: CognitoUserAttribute[] | undefined) => {
                const email = attrs?.find(a => a.Name === 'email')?.Value || mfaUser.getUsername();
                // Server switches the recovery email code off, closes any
                // recovery and issues fresh recovery codes to show once.
                let codes: string[] | null = null;
                let completeError: string | null = null;
                try {
                  codes = (await staffApi.security.complete()).codes;
                } catch (e: any) {
                  completeError = `Authenticator is set up, but recovery codes could not be created (${e?.message || 'error'}). Create them from My Security.`;
                }
                setState({
                  isAuthenticated: !codes,
                  isLoading: false,
                  user: mfaUser,
                  email,
                  mfaRequired: false,
                  newPasswordRequired: false,
                  mfaSetupRequired: false,
                  mfaSetupSecret: null,
                  emailCodeRequired: false,
                  emailCodeDestination: null,
                  recoveryCodes: codes,
                  error: completeError,
                });
                setMfaUser(null);
                resolve();
              });
            });
          });
        },
        onFailure: (err: Error) => {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: err.message || 'Invalid verification code',
          }));
          reject(err);
        },
      });
    });
  }, [mfaUser]);

  // Request a password-reset code be sent to the account's verified email
  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    setState((s) => ({ ...s, isLoading: true, error: null }));

    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    return new Promise((resolve, reject) => {
      user.forgotPassword({
        onSuccess: () => {
          setState((s) => ({ ...s, isLoading: false }));
          resolve();
        },

        onFailure: (err) => {
          // Deliberately not setting state.error here: forgotPassword can
          // fail with UserNotFoundException, and the top-level error banner
          // is shared UI - surfacing it there would leak account existence.
          // LoginPage catches the rejection and decides what to show.
          setState((s) => ({ ...s, isLoading: false }));
          reject(err);
        },

        inputVerificationCode: () => {
          setState((s) => ({ ...s, isLoading: false }));
          resolve();
        },
      });
    });
  }, []);

  // Confirm a password reset using the code emailed by forgotPassword
  const confirmForgotPassword = useCallback(async (email: string, code: string, newPassword: string): Promise<void> => {
    setState((s) => ({ ...s, isLoading: true, error: null }));

    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    return new Promise((resolve, reject) => {
      user.confirmPassword(code, newPassword, {
        onSuccess: () => {
          setState((s) => ({ ...s, isLoading: false }));
          resolve();
        },

        onFailure: (err) => {
          // Same reasoning as forgotPassword above: keep this local to the
          // reset form rather than the shared error banner.
          setState((s) => ({ ...s, isLoading: false }));
          reject(err);
        },
      });
    });
  }, []);

  // Logout function
  const logout = useCallback(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }

    // Clear tokens from localStorage
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');

    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      email: null,
      mfaRequired: false,
      newPasswordRequired: false,
      mfaSetupRequired: false,
      mfaSetupSecret: null,
      emailCodeRequired: false,
      emailCodeDestination: null,
      recoveryCodes: null,
      error: null,
    });
    setMfaUser(null);
    setNewPasswordUser(null);
  }, []);

  // Get ID token for API calls (Cognito authorizers require ID token)
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) return null;

    return new Promise((resolve) => {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) {
          console.log('[AuthContext] Session invalid or error:', err?.message);
          resolve(null);
          return;
        }
        // Use ID token for Cognito authorizer (not access token)
        const idToken = session.getIdToken().getJwtToken();
        console.log('[AuthContext] Got ID token:', idToken.substring(0, 30) + '...');
        resolve(idToken);
      });
    });
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    verifyMFA,
    completeNewPassword,
    verifyMfaSetup,
    verifyEmailCode,
    acknowledgeRecoveryCodes,
    forgotPassword,
    confirmForgotPassword,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
